import { getAgencyServicePromiseClient } from '../utils/grpcClient';
import {
    GetAgencyTrainerClientRelationsRequest,
    GetAgencyTrainerClientRelationsResponse,
    TrainerWithClients,
} from '../generated/fitness-service/v1/agency_pb';
import { TrainerClientRelation, ClientProfile } from '../generated/fitness-service/v1/common_pb';

export interface GetAgencyRelationsParams {
    page?: number;
    limit?: number;
    activeOnly?: boolean;
    trainerId?: string; // Optional filter by specific trainer
}

export interface TrainerClientData {
    relationshipId: string;
    client: ClientData;
    startDate: string;
    endDate?: string;
    isActive: boolean;
    notes: string;
    trainerCode: string;
}

export interface ClientData {
    id: string;
    userId: string;
    name: string;
    email: string;
    phone: string;
    dateOfBirth?: string;
    gender: string;
    isActive: boolean;
    createdAt: string;
    joinDate: string;
    trainerCount: number;
}

export interface TrainerWithClientsData {
    trainer: TrainerData;
    clientRelations: TrainerClientData[];
    activeClientCount: number;
    totalClientCount: number;
}

export interface TrainerData {
    id: string;
    userId: string;
    agencyId: string;
    name: string;
    email: string;
    phone: string;
    specialization: string;
    certification: string;
    experienceYears: number;
    joinDate: string;
    isActive: boolean;
}

export interface GetAgencyRelationsResult {
    trainerRelations: TrainerWithClientsData[];
    totalCount: number;
    page: number;
    limit: number;
}

export class AgencyRelationshipService {
    private client = getAgencyServicePromiseClient();

    private getAuthMetadata() {
        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new Error('No access token found');
        }
        return { authorization: `Bearer ${token}` };
    }

    private convertClientProfile(client: ClientProfile.AsObject): ClientData {
        return {
            id: client.id,
            userId: client.userId,
            name: client.name,
            email: client.email,
            phone: client.phone,
            dateOfBirth: client.dateOfBirth || undefined,
            gender: client.gender,
            isActive: client.isActive,
            createdAt: client.createdAt,
            joinDate: client.joinDate,
            trainerCount: client.trainerCount,
        };
    }

    private convertTrainerClientRelation(relation: TrainerClientRelation.AsObject): TrainerClientData {
        return {
            relationshipId: relation.relationshipId,
            client: this.convertClientProfile(relation.client!),
            startDate: relation.startDate,
            endDate: relation.endDate || undefined,
            isActive: relation.isActive,
            notes: relation.notes,
            trainerCode: relation.trainerCode,
        };
    }

    private convertTrainerData(trainer: any): TrainerData {
        return {
            id: trainer.id,
            userId: trainer.userId,
            agencyId: trainer.agencyId,
            name: trainer.name,
            email: trainer.email,
            phone: trainer.phone,
            specialization: trainer.specialization,
            certification: trainer.certification,
            experienceYears: trainer.experienceYears,
            joinDate: trainer.joinDate,
            isActive: trainer.isActive,
        };
    }

    private convertTrainerWithClients(trainerRelation: TrainerWithClients.AsObject): TrainerWithClientsData {
        return {
            trainer: this.convertTrainerData(trainerRelation.trainer!),
            clientRelations: trainerRelation.clientRelationsList.map(rel =>
                this.convertTrainerClientRelation(rel)
            ),
            activeClientCount: trainerRelation.activeClientCount,
            totalClientCount: trainerRelation.totalClientCount,
        };
    }

    /**
     * Get all trainer-client relationships for the agency
     */
    async getAgencyTrainerClientRelations(params: GetAgencyRelationsParams = {}): Promise<GetAgencyRelationsResult> {
        try {
            const request = new GetAgencyTrainerClientRelationsRequest();
            request.setPage(params.page || 1);
            request.setLimit(params.limit || 10);
            request.setActiveOnly(params.activeOnly ?? true);

            if (params.trainerId) {
                request.setTrainerId(params.trainerId);
            }

            const response = await this.client.getAgencyTrainerClientRelations(request, this.getAuthMetadata());
            const result = response.toObject();

            return {
                trainerRelations: result.trainerRelationsList.map(trainerRel =>
                    this.convertTrainerWithClients(trainerRel)
                ),
                totalCount: result.totalCount,
                page: result.page,
                limit: result.limit,
            };
        } catch (error: any) {
            console.error('Error fetching agency trainer-client relations:', error);
            throw new Error(error.message || 'Failed to fetch trainer-client relationships');
        }
    }

    /**
     * Get trainer statistics for dashboard
     */
    async getTrainerStats(): Promise<{
        totalTrainers: number;
        totalActiveClients: number;
        totalRelationships: number;
        avgClientsPerTrainer: number;
    }> {
        try {
            const relations = await this.getAgencyTrainerClientRelations({
                page: 1,
                limit: 100, // Get more data for stats
                activeOnly: false, // Include all relationships
            });

            const totalTrainers = relations.trainerRelations.length;
            const totalActiveClients = relations.trainerRelations.reduce(
                (sum, trainer) => sum + trainer.activeClientCount, 0
            );
            const totalRelationships = relations.trainerRelations.reduce(
                (sum, trainer) => sum + trainer.totalClientCount, 0
            );
            const avgClientsPerTrainer = totalTrainers > 0 ? totalActiveClients / totalTrainers : 0;

            return {
                totalTrainers,
                totalActiveClients,
                totalRelationships,
                avgClientsPerTrainer: Math.round(avgClientsPerTrainer * 100) / 100, // Round to 2 decimal places
            };
        } catch (error: any) {
            console.error('Error fetching trainer stats:', error);
            throw new Error(error.message || 'Failed to fetch trainer statistics');
        }
    }

    /**
     * Search clients across all trainers
     */
    async searchClients(searchTerm: string): Promise<TrainerClientData[]> {
        try {
            const relations = await this.getAgencyTrainerClientRelations({
                page: 1,
                limit: 100,
                activeOnly: true,
            });

            const allClients: TrainerClientData[] = [];
            relations.trainerRelations.forEach(trainer => {
                allClients.push(...trainer.clientRelations);
            });

            // Filter clients based on search term
            const filteredClients = allClients.filter(relation => {
                const client = relation.client;
                const searchLower = searchTerm.toLowerCase();
                return (
                    client.name.toLowerCase().includes(searchLower) ||
                    client.email.toLowerCase().includes(searchLower) ||
                    client.phone.includes(searchTerm)
                );
            });

            return filteredClients;
        } catch (error: any) {
            console.error('Error searching clients:', error);
            throw new Error(error.message || 'Failed to search clients');
        }
    }
}

export const agencyRelationshipService = new AgencyRelationshipService();
